import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Upload, Trash2, ChevronDown } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import Button from './Button';
import supabase from '../lib/supabase';

const schema = z.object({
  name: z.string().min(3, 'Name must be at least 3 characters'),
  description: z.string()
    .min(100, 'Description must be at least 100 characters')
    .max(300, 'Description cannot exceed 300 characters'),
  isPrivate: z.boolean().default(false),
  location: z.string().optional(),
  locationEnabled: z.boolean().default(false),
  genres: z.array(z.string()).optional()
});

type FormData = z.infer<typeof schema>;

interface CreateCommunityModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const AVAILABLE_GENRES = [
  'None',
  'Acoustic',
  'Acoustic Guitar',
  'Adult Alternative',
  'Adult Contemporary',
  'Adult Urban Contemporary',
  'African Music',
  'Afrikaans',
  'Afro-Beat',
  'Afro-Pop',
  'Alt Country',
  'Alt Rock',
  'Alternative',
  'Alternative Metal',
  'Alternativo en Español',
  'Ambient',
  'Americana',
  'Anime',
  'Arabesque',
  'Arabic',
  'Arabic Pop',
  'Arena Rock',
  'Aussie Hip-Hop/Rap',
  'Australian',
  'Austrian',
  'Avant-Garde Jazz',
  'Axé',
  'Bachata',
  'Baile Funk',
  'Bakersfield Sound',
  'Banda',
  'Baroque Era',
  'Bashment',
  'Bass',
  'Bebop',
  'Belgian',
  'Big Band',
  'Black Metal',
  'Blackgaze',
  'Bluegrass',
  'Blues',
  'Boleros',
  'Bollywood',
  'Bossa Nova',
  'Brazilian',
  'Breakbeat',
  'British',
  'British Invasion',
  'British Metal',
  'Britpop',
  'C-Pop',
  'Calypso',
  'Canadian',
  'Cantautori',
  'Cantopop',
  'Caribbean',
  'Celtic',
  'Chamber Music',
  'Chicago Blues',
  'Chillwave',
  'Chinese',
  'Chinese Alt',
  'Chinese Hip-Hop',
  'Chinese Rock',
  'Choral',
  'Christian',
  'Christian Country',
  'Christian Pop',
  'Christian Rap',
  'Christian Rock',
  'Classic Blues',
  'Classic Country',
  'Classic Metal',
  'Classic Rock',
  'Classic Singer/Songwriter',
  'Classical',
  'Classical Guitar',
  'Coffee Shop',
  'College Rock',
  'Comedy',
  'Contemporary Blues',
  'Contemporary Classical',
  'Contemporary Gospel',
  'Contemporary Jazz',
  'Corridos',
  'Country Rock',
  'Country',
  'Countrypolitan',
  'Crunk',
  'Cuban',
  'Cubaton',
  'Cumbia',
  'Dance',
  'Dancehall',
  'Dangdut',
  'Danish',
  'DarkWave',
  'Death Metal',
  'Deathcore',
  'Deep House',
  'Delta Blues',
  'Deutscher Streetrap',
  'Deutschpop',
  'Devotional & Spiritual',
  'Dirty South',
  'Disco',
  'Disney',
  'Dixieland',
  'Doo Wop',
  'Doom',
  'Downtempo',
  'Dub',
  'Dubstep',
  'Duranguense',
  'Dutch',
  'Dutch Pop',
  'East Coast',
  'Easy Listening',
  'Egyptian Pop',
  'Electric Blues',
  'Electro',
  'Electronic',
  'Electronica',
  'EMO',
  'Enka',
  'Experimental',
  'Extreme Metal',
  'Fado',
  'Farsi',
  'Filipino',
  'Finnish',
  'Flamenco',
  'Folk',
  'Folk-Rock',
  'Forró',
  'Freestyle',
  'French',
  'French Hip-Hop/Rap',
  'French Pop',
  'French Variété',
  'Funk',
  'Fusion Jazz',
  'Gangsta Rap',
  'Garage',
  'German',
  'German Folk',
  'Ghazals',
  'Glam Rock',
  'Golden Age',
  'Gospel',
  'Goth',
  'Grime',
  'Grindcore',
  'Grunge',
  'Hair Metal',
  'Halk',
  'Hanukkah',
  'Hard Bop',
  'Hard Rock',
  'Hardcore Punk',
  'Hawaiian',
  'Hindi',
  'Hip-Hop en Espańol',
  'Hip-Hop/Rap',
  'Hip-Hop',
  'Hip-Hop/Rap Italiano',
  'Holiday',
  'Honky Tonk',
  'Hörspiele',
  'House',
  'Hyphy',
  'IDM',
  'Indian',
  'Indian Classical',
  'Indian Folk',
  'Indian Pop',
  'Indie',
  'Indie Dance',
  'Indie en Espańol',
  'Indie Pop',
  'Indie R&B',
  'Indie Rock',
  'Indonesian Music',
  'Industrial Metal',
  'Inspirational',
  'Instrumental',
  'Irish',
  'Israeli',
  'Israeli Alternative',
  'Israeli Pop',
  'Israeli Rock',
  'Italian',
  'J-Hip-Hop',
  'J-Pop',
  'J-R&B',
  'J-Rock',
  'Jam Bands',
  'Japanese',
  'Jazz',
  'Jazz Vocal',
  'Jungle',
  'K Hip-Hop',
  'K Rap',
  'K-Indie',
  'K-Pop',
  'K-R&B',
  'K-Rock',
  'Kannada',
  'Karaoke',
  'Kayokyoku',
  'Khaleeji',
  'Khaleeji Pop',
  'Khaleeji Tarab',
  'Kids & Family',
  'Korean',
  'Kwanzaa',
  'Latin Jazz',
  'Latino',
  'Levant Pop',
  'Lounge',
  'Lovers Rock',
  'Lullabies',
  'Malayalam',
  'Malaysian Music',
  'Malaysian Pop',
  'Mandopop',
  'Marathi',
  'Mariachi',
  'Memphis Soul',
  'Merengue',
  'Metal',
  'Metalcore',
  'Mexican',
  'Minimalism',
  'Mizrahi',
  'Mod',
  'Modern Dancehall',
  'Modern Era',
  'Motown',
  'MPB',
  'Música Mexicana',
  'Música Religios',
  'Musicals',
  'Musique Francophone',
  'Muzak',
  'Nature',
  'Neo-Soul',
  'New Age',
  'New Jack Swing',
  'New Romantic',
  'New Wave',
  'Norteño',
  'Norwegian',
  'Nu Metal',
  'Nueva Canción',
  'Nursery Rhymes',
  'Old School',
  'Oldies',
  'Opera',
  'Orchestral',
  'Outlaw Country',
  'Pagode',
  'Piano',
  'Pinoy Pop',
  'Polka',
  'Pop',
  'Pop in Portuguese',
  'Pop Italiano',
  'Pop Latino',
  'Pop Punk',
  'Pop Rock',
  'Portugeuse',
  'Post-Grunge',
  'Post-Punk',
  'Post-Rock',
  'Power Pop',
  'Praise & Worship',
  'Prog-Rock',
  'Progressive House',
  'Psych Rock',
  'Punjabi',
  'Punk',
  'Quebec',
  'Quiet Storm',
  'R&B',
  'Ragtime',
  'Rancheras',
  'Rap',
  'Rave',
  'Reggae',
  'Reggaeton',
  'Regional Indian',
  'Relaxation',
  'Renaissance',
  'Requinto',
  'Rock',
  'Rock in Portuguese',
  'Rock en Español',
  'Rockabilly',
  'Romantic Era',
  'Roots Reggae',
  'Roots Rock',
  'Russian',
  'Russian Alternative',
  'Russian Chanson',
  'Russian Hip-Hop',
  'Russian Pop',
  'Russian Rock',
  'Salsa',
  'Samba',
  'Sanat',
  'Saxophone',
  'Schlager',
  'Scores',
  'Scottish',
  'Sertanejo',
  'Shoegaze',
  'Singer/Songwriter',
  'Ska',
  'Sludge',
  'Smooth Jazz',
  'Soca',
  'Soft Rock',
  'Son Jarocho',
  'Sonidero',
  'Soul',
  'Soul Jazz',
  'Southern Gospel',
  'Southern Rock',
  'South American',
  'Spanish',
  'Stage and Screen',
  'Standards',
  'Stoner Rock',
  'Strings',
  'Sufi & Ghazals',
  'Surf',
  'Swiss',
  'Synth-Pop',
  'Tai-Pop',
  'Tamil',
  'Tango',
  'Tarab',
  'Techno',
  'Teen Pop',
  'Telugu',
  'Thai Music',
  'Thai Pop',
  'Third Wave Ska',
  'Thrash Metal',
  'Tololoche',
  'Traditional Bluegrass',
  'Traditional Country',
  'Traditional Folk',
  'Trance',
  'Trap',
  'Trap Latino',
  'Tropical House',
  'Trova',
  'Turkish',
  'UK Garage',
  'UK Hip-Hop/Rap',
  'Underground Hip-Hop/Rap',
  'Urbano Latino',
  'Vallenato',
  'Vietnamese',
  'West Coast',
  'Worldbeat',
  'Yacht Rock',
  'Christmas',
  'Halloween',
  "Children's Music",
  'Fitness & Workout',
  'Soundtrack',
  'Spoken Word',
  'Vocal',
  'Christian & Gospel',
  'Música Tropical',
  'Rock Y Alternativo',
  'Soul/Funk',
  'World'
];

export default function CreateCommunityModal({ onClose, onSuccess }: CreateCommunityModalProps) {
  const { user } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [isGenreDropdownOpen, setIsGenreDropdownOpen] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema)
  });

  // Helper function to check if URL is from Supabase storage
  const isSupabaseStorageUrl = (url: string): boolean => {
    return url.includes('supabase') && url.includes('/storage/');
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Image size must be less than 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('File must be an image');
        return;
      }

      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        // Force re-render with timestamp to avoid cache issues during preview
        setCoverPreview(`${reader.result as string}#${Date.now()}`);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleGenre = (genre: string) => {
    setSelectedGenres(prev => {
      if (prev.includes(genre)) {
        return prev.filter(g => g !== genre);
      } else {
        return [...prev, genre];
      }
    });
  };

  const onSubmit = async (data: FormData) => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // Create community
      const { data: community, error: communityError } = await supabase
        .from('communities')
        .insert({
          name: data.name,
          description: data.description,
          is_private: data.isPrivate,
          leader_id: user.id,
          genres: selectedGenres,
          location: data.location,
          location_enabled: data.locationEnabled
        })
        .select()
        .single();

      if (communityError) throw communityError;

      // Upload cover image if provided
      if (coverImage && community) {
        const fileExt = coverImage.name.split('.').pop();
        const filePath = `${community.id}/cover.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('community-covers')
          .upload(filePath, coverImage, {
            cacheControl: '3600',
            upsert: true, // Allow overwriting
            contentType: coverImage.type,
          });

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('community-covers')
          .getPublicUrl(filePath);

        // Add cache busting parameter to force browser refresh
        const cacheBustedUrl = isSupabaseStorageUrl(publicUrl) 
          ? `${publicUrl}?v=${Date.now()}` 
          : publicUrl;

        // Update community with cover image URL
        const { error: updateError } = await supabase
          .from('communities')
          .update({ cover_image: cacheBustedUrl })
          .eq('id', community.id);

        if (updateError) throw updateError;
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating community:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-10">
      <div className="bg-surface-light rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto space-y-4">
        <div className="sticky top-0 bg-neutral border-b border-neutral p-4 flex items-start justify-between shadow-md z-10">
          <h2 className="text-3xl font-bold font-dillan text-secondary">Create Community</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-light rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-neutral" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-6">
          {error && (
            <div className="bg-secondary-dark bg-opacity-10 text-secondary-dark p-4 rounded-xl">
              {error}
            </div>
          )}

          <div className="flex justify-center relative">
            <div className="w-full">
              {coverPreview ? (
                <div className="flex justify-center relative">
                  <img
                    src={coverPreview}
                    alt="Cover preview"
                    className="w-full h-32 rounded-t-xl object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setCoverImage(null);
                      setCoverPreview(null);
                    }}
                    className="absolute top-2 right-2 p-1 bg-primary text-surface-light rounded-full hover:bg-secondary transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="w-full h-32 border-2 border-primary border-dashed rounded-t-xl relative text-center">
                  <label
                    htmlFor="cover-image"
                    className="cursor-pointer group"
                  >
                    <Upload className="mx-auto h-8 w-8 text-primary group-hover:text-secondary transition-colors mt-8" />
                    <span className="text-sm text-center text-primary">Upload cover image</span>
                    <input
                      id="cover-image"
                      name="cover-image"
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          <div>
            <input
              type="text"
              id="name"
              {...register('name')}
              className="w-full px-3 py-2 bg-secondary border border-secondary rounded-full text-primary focus:outline-none focus:ring-0 focus:ring-secondary placeholder:text-primary font-dillan"
              placeholder="Community Name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-primary pl-4">{errors.name.message}</p>
            )}
          </div>

          <div>
            <textarea
              id="description"
              rows={2}
              {...register('description')}
              className="w-full px-3 py-2 pl-4 bg-secondary text-primary rounded-full focus:outline-none focus:ring-0 focus:ring-primary resize-none placeholder:text-primary"
              placeholder="Description"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-primary pl-4">{errors.description.message}</p>
            )}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => setIsGenreDropdownOpen(!isGenreDropdownOpen)}
              className="w-full px-3 py-2 text-primary bg-secondary border border-secondary rounded-full focus:outline-none focus:ring-0 focus:ring-primary text-left flex justify-between items-center"
            >
              <span>
                {selectedGenres.length === 0 
                  ? 'Select genre(s)' 
                  : `${selectedGenres.length} genre${selectedGenres.length === 1 ? '' : 's'} selected`
                }
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isGenreDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isGenreDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-secondary border border-seconday rounded-lg shadow-lg max-h-60 overflow-y-auto text-primary font-dillan">
                {AVAILABLE_GENRES.map((genre) => (
                  <label
                    key={genre}
                    className="flex items-center px-3 py-2 hover:bg-primary hover:text-secondary cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedGenres.includes(genre)}
                      onChange={() => toggleGenre(genre)}
                      className="w-2 h-2 border-2 border-primary rounded-full 
      appearance-none transition-all 
      checked:bg-primary checked:border-primary
      hover:border-secondary
      hover:checked:bg-secondary hover:checked:border-secondary"
                    />
                    <span className="text-sm pl-2">{genre}</span>
                  </label>
                ))}
              </div>
            )}
            
            {selectedGenres.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2 pl-2">
                {selectedGenres.map((genre) => (
                  <span
                    key={genre}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary text-surface-light"
                  >
                    {genre}
                    <button
                      type="button"
                      onClick={() => toggleGenre(genre)}
                      className="ml-1 hover:text-surface-light"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="space-y-2">
              <div className="flex items-center pl-2">
                <input
                  type="checkbox"
                  id="locationEnabled"
                  {...register('locationEnabled')}
                  className="w-4 h-4 pl-2 border-2 border-primary rounded-full 
      appearance-none transition-all 
      checked:bg-primary checked:border-primary"
                />
                <label htmlFor="locationEnabled" className="ml-2 text-sm text-primary">
                  Enable location for this community
                </label>
              </div>
              <input
                type="text"
                id="location"
                {...register('location')}
                placeholder="e.g. London, United Kingdom"
                className="w-full px-3 py-2 bg-secondary text-primary placeholder:text-primary border border-secondary rounded-full focus:outline-none focus:ring-0 focus:ring-primary"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center space-x-2 pl-2">
              <input
                type="checkbox"
                {...register('isPrivate')}
                className="w-4 h-4 pl-2 border-2 border-primary rounded-full 
      appearance-none transition-all 
      checked:bg-primary checked:border-primary"
              />
              <span className="text-sm text-primary">Make this community private</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4 mt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="bg-neutral text-secondary hover:bg-primary-dark hover:text-primary border border-neutral hover:border-primary-dark"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting}
              className="bg-neutral text-secondary hover:bg-primary-dark hover:text-primary border border-neutral hover:border-primary-dark"
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}